import * as React from "react";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";

import { getSessionToken } from "@/common/auth";
import {
  fetchGatewayIdentity,
  getGatewaySessionToken,
} from "@/common/gateway-session";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/Button";
import { CliNav } from "@/landing/components/CliNav";
import { MinimalFooter } from "@/landing/components/MinimalFooter";
import { api } from "@/trpc/react";

const JWT_RETRY_INTERVAL_MS = 2000;
const JWT_RETRY_BUDGET_MS = 30000;

export const Route = createFileRoute(
  "/_authenticated/(wonda)/_wonda/onboarding",
)({
  beforeLoad: async () => {
    // Bounce to /account until userinfo reports an accountId (/account owns
    // the long "Setting up your account..." wait with a retry button. Polling
    // here would keep this route in pending status long enough for TanStack
    // Router's loadPromise to be torn down by RouterContextSyncInner
    // re-renders, surfacing as `throw undefined`).
    const identity = await fetchGatewayIdentity();
    if (!identity?.publicMetadata.accountId) {
      throw redirect({ to: "/account" });
    }
    // The account can be provisioned a tick before the cached access token
    // reflects it. Force a fresh /session/token so child beforeLoads
    // (getWelcomeAnswers) send a bearer that resolves the account.
    try {
      await getGatewaySessionToken({ skipCache: true });
    } catch {
      // Non-fatal: child queries retry on their own.
    }
  },
  component: OnboardingLayout,
});

function OnboardingLayout() {
  const posthog = usePostHog();

  const {
    isLoading: isAnswersLoading,
    isError: isAnswersError,
    refetch: refetchAnswers,
  } = api.account.getWelcomeAnswers.useQuery();

  // Right after signup, the session-token endpoint can keep serving a bearer
  // that does not resolve the account for a few seconds even after
  // provisioning landed and beforeLoad forced a refresh. A single retry races
  // the same lag and often loses. Poll instead: every 2s mint a fresh token
  // and refetch, until the query succeeds or the 30s wall-time budget is hit.
  // The budget is wall-time (not attempt count) so slow or stuck requests
  // can't extend the wait indefinitely.
  const [isRetryBudgetExhausted, setIsRetryBudgetExhausted] =
    React.useState(false);
  React.useEffect(() => {
    if (!isAnswersError || isRetryBudgetExhausted) return;
    const budgetTimer = setTimeout(() => {
      setIsRetryBudgetExhausted(true);
    }, JWT_RETRY_BUDGET_MS);
    const interval = setInterval(() => {
      void (async () => {
        await getSessionToken({ skipCache: true });
        await refetchAnswers();
      })();
    }, JWT_RETRY_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(budgetTimer);
    };
  }, [isAnswersError, isRetryBudgetExhausted, refetchAnswers]);

  const hasTrackedFlowStart = React.useRef(false);
  React.useEffect(() => {
    if (hasTrackedFlowStart.current) return;
    hasTrackedFlowStart.current = true;
    posthog.capture("welcome flow started", {
      hardPaywall: true,
      valueStack: false,
    });
  }, [posthog]);

  if (isAnswersLoading || (isAnswersError && !isRetryBudgetExhausted)) {
    return (
      <OnboardingShell>
        <main className="flex flex-1 flex-col items-center justify-center gap-4">
          <Spinner variant="wonda" />
        </main>
      </OnboardingShell>
    );
  }

  // 30s of JWT-refetch retries didn't recover. Falling through to <Outlet />
  // sends the user to a child route whose loader will 401 in a loop. Show a
  // recoverable error instead.
  if (isAnswersError && isRetryBudgetExhausted) {
    return (
      <OnboardingShell>
        <main className="flex flex-1 flex-col items-center justify-center gap-4 px-4 text-center">
          <p className="text-base">
            We couldn&apos;t load your account. Reload to try again.
          </p>
          <Button onClick={() => window.location.reload()}>Reload</Button>
        </main>
      </OnboardingShell>
    );
  }

  return (
    <OnboardingShell>
      <Outlet />
    </OnboardingShell>
  );
}

// Shell shared by every onboarding branch. The light
// ground comes from the global html/body base paint.
function OnboardingShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-[100dvh] flex-col">
      <CliNav />
      {children}
      <MinimalFooter />
    </div>
  );
}
